import { useState, useCallback } from 'react';
import { useSnackbarNotification } from '@/hooks/useSnackbarNotification';
import { getServicesApi } from '@/services/bolApiClient';
import { extractErrorMessage, formatOperationError } from '@/utils/errorUtils';
import { getLogisticsObjectDetailPath } from '@/types/logisticsObject';
import { 
  Service, 
  SearchServicesRequest, 
  ACLAuthorizationRequest,
  ServiceResponse,
  SearchServicesPostContentTypeEnum,
  CreateServiceContentTypeEnum
} from '@/api/bol';
import { getEffectiveLogisticsObjectSearchQuery } from '@/utils/searchDefaults';

export interface UseServicesReturn {
  services: ServiceResponse[];
  loading: boolean;
  searchError: string | null;
  selectedService: ServiceResponse | null;
  setSelectedService: (service: ServiceResponse | null) => void;
  fetchServices: (page?: number, pageSize?: number, searchBody?: SearchServicesRequest) => Promise<boolean>;
  createService: (serviceData: unknown, memberIdentifier: string, aclAuthorizations?: ACLAuthorizationRequest[]) => Promise<void>;
  deleteService: (serviceId: string) => Promise<void>;
  setTotal: (total: number) => void;
  total: number;
}

export const useServices = (): UseServicesReturn => {
  const [services, setServices] = useState<ServiceResponse[]>([]);
  const [loading, setLoading] = useState(false);
  const [searchError, setSearchError] = useState<string | null>(null);
  const [selectedService, setSelectedService] = useState<ServiceResponse | null>(null);
  const [total, setTotal] = useState(0);
  
  const { showSuccess, showError } = useSnackbarNotification();

  const fetchServices = useCallback(async (page: number = 0, pageSize: number = 10, searchBody?: SearchServicesRequest): Promise<boolean> => {
    const normalizedSearchBody = getEffectiveLogisticsObjectSearchQuery(searchBody);

    console.log('Fetching services with:', { page, pageSize, searchBody: normalizedSearchBody });

    setLoading(true);
    setSearchError(null);
    
    try {
      const api = getServicesApi();
      
      const response = await api.searchServicesPost({
        contentType: SearchServicesPostContentTypeEnum.ApplicationJson,
        searchServicesRequest: normalizedSearchBody,
        limit: pageSize,
        offset: page * pageSize
      });
      
      console.log('Services API response:', response);
      
      setServices(response.results || []);
      setTotal(response.pagination?.total || 0);
      return true;
    } catch (err: unknown) {
      console.error('Error fetching services:', err);
      const errorMessage = await extractErrorMessage(err, 'Failed to fetch services');
      setSearchError(errorMessage);
      showError(errorMessage);
      return false;
    } finally {
      setLoading(false);
    }
  }, [showError]);

  const createService = useCallback(async (serviceData: unknown, memberIdentifier: string, aclAuthorizations?: ACLAuthorizationRequest[]) => {
    try {
      console.log('Creating service with data:', serviceData, 'Member:', memberIdentifier, 'ACL:', aclAuthorizations);
      
      const api = getServicesApi();
      
      const createdService = await api.createService({
        contentType: CreateServiceContentTypeEnum.ApplicationJson,
        xBOLMemberIdentifier: memberIdentifier,
        service: serviceData as Service,
        source: true,
        xBOLACLAuthorizations: aclAuthorizations ? JSON.stringify(aclAuthorizations) : undefined
      });
      
      console.log('Service created successfully:', createdService);
      const createdServiceId = createdService?.id;
      
      showSuccess(
        'Service created successfully',
        createdServiceId
          ? {
              link: {
                label: 'View details',
                href: getLogisticsObjectDetailPath('service', createdServiceId),
              },
            }
          : undefined,
      );
    } catch (err: unknown) {
      console.error('Error creating service:', err);
      const errorMessage = await formatOperationError(err, 'Create service', 'Failed to create service');
      showError(errorMessage);
      throw err; // Re-throw to let the calling component handle form state
    }
  }, [showSuccess, showError]);

  const deleteService = useCallback(async (serviceId: string) => {
    try {
      const api = getServicesApi();
      
      await api.deleteServiceById({
        serviceId
      });
      
      showSuccess('Service deleted successfully');
    } catch (err: unknown) {
      console.error('Error deleting service:', err);
      const errorMessage = await formatOperationError(err, 'Delete service', 'Failed to delete service');
      showError(errorMessage);
      throw err; // Re-throw to let the calling component handle dialog state
    }
  }, [showSuccess, showError]);

  return {
    services,
    loading,
    searchError,
    selectedService,
    setSelectedService,
    fetchServices,
    createService,
    deleteService,
    setTotal,
    total
  };
};